import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import { InlineLoader } from '../../components/common/Loader';
import { getAllAppointments } from '../../services/adminService';
import { formatDate, formatTime12, formatCurrency, statusBadgeClass, statusLabel, todayISO } from '../../utils/helpers';

export default function AdminAppointments() {
  const [appointments, setAppts] = useState([]);
  const [loading, setLoading]    = useState(true);
  const [total, setTotal]        = useState(0);
  const [page, setPage]          = useState(1);
  const [filters, setFilters]    = useState({
    date: todayISO(), type: '', status: '',
  });

  const load = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: 25 };
      if (filters.date)   params.date   = filters.date;
      if (filters.type)   params.type   = filters.type;
      if (filters.status) params.status = filters.status;
      const res = await getAllAppointments(params);
      if (pg === 1) setAppts(res.appointments || []);
      else setAppts(p => [...p, ...(res.appointments || [])]);
      setTotal(res.total || 0);
      setPage(pg);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(1); }, [load]);

  const setF = (k, v) => setFilters(p => ({ ...p, [k]: v }));

  return (
    <AdminLayout title="Appointments Overview">
      <div className="space-y-4">

        {/* Filters */}
        <div className="flex gap-3 flex-wrap items-end">
          <div>
            <label className="input-label">Date</label>
            <input type="date" value={filters.date}
              onChange={e => setF('date', e.target.value)}
              className="input w-40" />
          </div>
          <div>
            <label className="input-label">Type</label>
            <select value={filters.type} onChange={e => setF('type', e.target.value)} className="input w-36">
              <option value="">All Types</option>
              <option value="clinic">Clinic</option>
              <option value="video">Video</option>
            </select>
          </div>
          <div>
            <label className="input-label">Status</label>
            <select value={filters.status} onChange={e => setF('status', e.target.value)} className="input w-40">
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="checked-in">Checked In</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <button onClick={() => setFilters({ date: '', type: '', status: '' })}
            className="px-4 py-2.5 border border-border rounded-btn text-sm text-text-secondary">
            Clear
          </button>
        </div>

        <p className="text-sm text-text-muted">{total} appointment{total !== 1 ? 's' : ''}</p>

        {loading && page === 1 ? <InlineLoader text="Loading..." /> : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-surface border-b border-border">
                  <tr>
                    {['Booking ID','Patient','Doctor','Date & Time','Type','Amount','Status'].map(h => (
                      <th key={h} className="px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {appointments.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-10 text-sm text-text-muted">No appointments found</td></tr>
                  ) : appointments.map(a => (
                    <tr key={a._id} className="border-b border-border hover:bg-surface">
                      <td className="px-4 py-3">
                        <p className="text-xs font-mono text-text-muted">{a.bookingId}</p>
                        <p className="text-xs text-text-muted">Token #{a.tokenNumber}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-text-primary">{a.patientId?.name || '—'}</p>
                        <p className="text-xs text-text-muted">{a.patientId?.phone}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-text-primary">{a.doctorId?.name || '—'}</p>
                        <p className="text-xs text-text-muted">{a.doctorId?.clinicName}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                        {formatDate(a.appointmentDate)}<br />
                        <span className="text-xs">{formatTime12(a.slotTime)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge ${a.type === 'video' ? 'badge-info' : 'badge-primary'}`}>
                          <i className={`fa-solid ${a.type === 'video' ? 'fa-video' : 'fa-hospital'} mr-1 text-[9px]`} />
                          {a.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-text-primary">
                        {a.paymentId ? formatCurrency(a.paymentId.totalAmount) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={statusBadgeClass(a.status)}>{statusLabel(a.status)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {appointments.length < total && (
          <button onClick={() => load(page + 1)} disabled={loading}
            className="w-full py-3 border border-border rounded-btn text-sm font-medium text-primary">
            Load More
          </button>
        )}
      </div>
    </AdminLayout>
  );
}
